import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type {
  LiveUpdatesConfiguration,
  LiveUpdatesStatus,
  LiveUpdatesStatusKey,
} from 'api/liveUpdates.types'
import HelpCenterLink from 'components/HelpCenterLink'
import OloWebhookBanner from 'components/OloWebhookBanner'
import { buildTranslate } from 'locales'
import React from 'react'
import { liveUpdatesHelpUrl } from 'utilities/urlUtils'
import { CONTENT_MAX_WIDTH, PAGE_MAX_WIDTH } from '../index.constants'
import { HEADER_HEIGHT_VAR, NAVBAR_HEIGHT } from '../useCollapsingHeader'
import type { UseLiveUpdatesConfiguration } from '../useLiveUpdatesConfiguration'
import ConfigurationPicker from './ConfigurationPicker'
import DetailStatusPlaceholder from './DetailStatusPlaceholder'
import LockedCard, { UNSUPPORTED_GROUP_KEYS } from './LockedCard'
import { LiveUpdatesPreview } from './preview'
import type { PreviewContent, TrackerStage } from './preview'
import PreviewDisabledCard from './PreviewDisabledCard'
import PushDisabledBanner from './PushDisabledBanner'
import ScheduledOrdersBanner from './ScheduledOrdersBanner'
import type { LiveUpdatesSectionPlan } from './sections'
import { stackChips } from './stackChips'
import StatusCard from './StatusCard'
import StatusSection from './StatusSection'
import type { OloWebhookConnectionState, OloWebhookPanel } from './trigger'

const t = buildTranslate('live_updates')
const tPicker = buildTranslate('live_updates.picker')
const tLocked = buildTranslate('live_updates.locked')
const tConflict = buildTranslate('live_updates.conflict')
const tErrors = buildTranslate('live_updates.errors')

const PREVIEW_WIDTH = '380px'
const COLUMN_GAP = '48px'
const PREVIEW_TOP_GAP_BELOW_DISABLED_BANNER = '24px'
const PREVIEW_BOTTOM_GAP = '24px'
const PREVIEW_BOTTOM_GAP_BELOW_DISABLED_BANNER = '48px'
const SECTION_STACK_GAP = '40px'
const CHIP_GAP = '20px'

// A section plan only carries a webhook gate while the connection is gated
// (not_connected or waiting), so 'connected' never reaches a gate card — but
// nothing in the type expresses that. Narrow by check rather than assertion:
// the impossible state yields no gate panel instead of a lie to the compiler.
const gatedConnectionState = (
  connectionState: OloWebhookConnectionState | undefined
): Exclude<OloWebhookConnectionState, 'connected'> | undefined =>
  connectionState === 'not_connected' || connectionState === 'waiting'
    ? connectionState
    : undefined

// Fallback matches the expanded header height for the first paint before the
// ResizeObserver publishes a measured value.
const previewTop = `calc(${NAVBAR_HEIGHT} + var(${HEADER_HEIGHT_VAR}, 131px))`

export interface ConfigurationTabProps {
  isPushDisabled: boolean
  configurations: LiveUpdatesConfiguration[]
  selectedId: string | null
  onSelectConfiguration: (selected: { value: string }[]) => void
  selectedConfiguration: LiveUpdatesConfiguration | null
  onViewLocations: () => void
  oloWebhook: OloWebhookPanel | undefined
  onManageOloWebhook: () => void
  isUnsupportedGroup: boolean
  warnings: string[]
  config: UseLiveUpdatesConfiguration
  successDismissed: boolean
  onDismissSuccess: () => void
  onReloadConfiguration: () => void
  sectionPlans: LiveUpdatesSectionPlan[]
  onFocusField: (status: LiveUpdatesStatusKey) => void
  previewContent: PreviewContent | null
  appIconUrl: string | undefined
  disabledStages: Set<TrackerStage>
  merchantName: string | undefined
  isFlybuyProvider: boolean
  /** Every field is read only; the picker stays usable. */
  isLiveUpdatesDisabled: boolean
  /** Turns live updates back on from the preview's lock card. Absent when the
   * viewer cannot, so the card carries no dead control. */
  onTurnOn?: () => void
  /** True while the SELECTED configuration's own detail (statuses) hasn't
   * caught up to a switch yet — `sectionPlans`/`previewContent` may be
   * empty/null, OR may still reflect the PREVIOUS configuration for one
   * render (RTK Query's `isLoading`-reads-false gap) — either way they
   * don't describe the configuration the picker now shows. Swaps both for a
   * scoped loading placeholder instead of rendering from either. */
  isDetailLoading: boolean
  /** True once the SELECTED configuration's detail will never become
   * editable/previewable as fetched — either a genuine fetch error, or a
   * detail that loaded but can't report a numeric version. Mutually
   * exclusive with `isDetailLoading`. Replaces the sections/preview with a
   * retry notice (via `onReloadConfiguration`) in the same slots, rather
   * than a spinner that would never resolve. */
  isDetailUnavailable: boolean
}

// The Configuration tab: the original single-pane Live Updates editor
// (picker, integration-stack chips, grouped status cards, sticky live
// preview) now repositioned alongside the new Overview tab.
const ConfigurationTab = ({
  isPushDisabled,
  configurations,
  selectedId,
  onSelectConfiguration,
  selectedConfiguration,
  onViewLocations,
  oloWebhook,
  onManageOloWebhook,
  isUnsupportedGroup,
  warnings,
  config,
  successDismissed,
  onDismissSuccess,
  onReloadConfiguration,
  sectionPlans,
  onFocusField,
  previewContent,
  appIconUrl,
  disabledStages,
  merchantName,
  isFlybuyProvider,
  isLiveUpdatesDisabled,
  onTurnOn,
  isDetailLoading,
  isDetailUnavailable,
}: ConfigurationTabProps) => {
  const [css] = useStyletron()

  const chips = selectedConfiguration
    ? stackChips(selectedConfiguration.stack)
    : []

  // Guards on `selectedConfiguration` being set — the null check here is just
  // to satisfy the type checker.
  const renderStatusCard = (status: LiveUpdatesStatus) => {
    if (!selectedConfiguration) return null
    return (
      <StatusCard
        key={status.status}
        status={status}
        stack={selectedConfiguration.stack}
        configurable={selectedConfiguration.configurable}
        disabled={config.isPublishing || isLiveUpdatesDisabled}
        pushDisabled={isPushDisabled}
        errors={config.publishError?.statusErrors[status.status]}
        oloWebhook={oloWebhook}
        onSetEnabled={config.setEnabled}
        onSetTriggerMode={config.setTriggerMode}
        onSetTimerOffset={config.setTimerOffset}
        onSetContent={config.setContent}
        onFocusField={onFocusField}
      />
    )
  }

  // The gate card's copy differs between "no secret saved" and "saved, still
  // waiting for the first event", so it needs the state alongside the section.
  const gateState = gatedConnectionState(oloWebhook?.connectionState)

  const renderSectionPlan = (plan: LiveUpdatesSectionPlan) => (
    <StatusSection
      key={plan.sectionKey}
      titleKey={plan.titleKey}
      noteKey={plan.noteKey}
      lockedMode={plan.lockedMode}
      webhookGate={
        plan.webhookGate && gateState
          ? {
              section: plan.webhookGate,
              connectionState: gateState,
              onSetUp: onManageOloWebhook,
            }
          : undefined
      }
      suppressedStatuses={plan.suppressedStatuses}
    >
      {/* A locked (FR-75) or webhook-gated section renders its own replacement
          card and ignores children — don't construct the cards at all. */}
      {plan.lockedMode || plan.webhookGate
        ? null
        : plan.statuses.map(renderStatusCard)}
    </StatusSection>
  )

  const renderPublishError = () => {
    const error = config.publishError
    if (!error) return null

    // The brand turned live updates off since this page loaded. The page
    // already shows the off state (banner, locked fields), so no error text.
    if (error.kind === 'live_updates_disabled') return null

    if (error.kind === 'conflict') {
      return (
        <Notification kind="negative" size="full">
          <span className="mr-s">{tConflict('message')}</span>
          <Button kind="minimal" onClick={onReloadConfiguration}>
            {tConflict('reload')}
          </Button>
        </Notification>
      )
    }

    // 'invalid' (400), 'unprocessable' (422), and 'unknown' all surface their
    // top-level messages when present (per-status field errors render on the
    // cards); a 400 whose messages are all per-status falls back to the generic
    // copy. Only 'conflict' (409) gets the dedicated reload affordance above.
    const message =
      error.messages.length > 0
        ? error.messages.join(' ')
        : tErrors('publish_failed')
    return (
      <Notification kind="negative" size="full">
        {message}
      </Notification>
    )
  }

  const renderPreviewBody = () => {
    if (isLiveUpdatesDisabled) {
      return <PreviewDisabledCard onTurnOn={onTurnOn} />
    }
    if (isDetailUnavailable) {
      return (
        <DetailStatusPlaceholder
          state="unavailable"
          data-testid="lu-configuration-preview-unavailable"
          retryAriaLabel={tErrors('retry_preview_aria')}
          onRetry={onReloadConfiguration}
        />
      )
    }
    if (!previewContent || isDetailLoading) {
      return (
        <DetailStatusPlaceholder
          state="loading"
          data-testid="lu-configuration-preview-loading"
          retryAriaLabel={tErrors('retry_preview_aria')}
          onRetry={onReloadConfiguration}
        />
      )
    }
    return (
      <LiveUpdatesPreview
        content={previewContent}
        deliveryCapable={!!selectedConfiguration?.deliveryConfigurable}
        appIconUrl={appIconUrl}
        disabledStages={disabledStages}
        locked={isUnsupportedGroup}
        pushDisabled={isPushDisabled}
        merchantName={merchantName}
        isFlybuyProvider={isFlybuyProvider}
      />
    )
  }

  return (
    <div
      className={css({
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        display: 'flex',
        gap: COLUMN_GAP,
        // No top padding: the preview column sits flush against the header
        // (Figma). The editor column adds its own top spacing below.
        padding: '0 24px 32px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      })}
    >
      <div
        className={css({
          flex: '1 1 480px',
          maxWidth: CONTENT_MAX_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          gap: SECTION_STACK_GAP,
          paddingTop: '32px',
        })}
      >
        {isPushDisabled && !isLiveUpdatesDisabled && <PushDisabledBanner />}

        {/* Configuration picker + view-locations + stack chips */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          })}
        >
          <ConfigurationPicker
            configurations={configurations}
            selectedId={selectedId}
            onSelectConfiguration={onSelectConfiguration}
            labelKey="label"
            ariaLabelKey="aria_label"
            disabled={config.isPublishing}
          >
            {selectedConfiguration && (
              <Button kind="minimal" onClick={onViewLocations}>
                {tPicker('view_locations')}
              </Button>
            )}
          </ConfigurationPicker>

          {chips.length > 0 && (
            <div
              data-testid="lu-stack-chips"
              className={css({
                display: 'flex',
                flexWrap: 'wrap',
                gap: CHIP_GAP,
              })}
            >
              {chips.map(chip => (
                <div
                  key={chip.label}
                  className={css({
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '6px',
                  })}
                >
                  <Text.BodyText5 color="grey70">{chip.label}</Text.BodyText5>
                  <Text.BodyText4 bold color="grey90">
                    {chip.name}
                  </Text.BodyText4>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Page-level Olo webhook connection banner (Figma): the single
            source of truth for connection health, directly under the picker
            + stack chips and above the first status section. `oloWebhook`
            already encodes "this configuration orders through Olo AND the
            credential/receipts queries have settled".
            Also suppressed for an unsupported (non-configurable) group: that
            renders the FR-76 billboard INSTEAD of any status sections, so a
            "connect webhooks to turn on live updates" prompt above it would
            promise something the billboard simultaneously says is
            unavailable. Not reachable while Olo is only ever a supported
            ordering provider — guarded rather than assumed. */}
        {oloWebhook && !isUnsupportedGroup && !isLiveUpdatesDisabled && (
          <OloWebhookBanner
            connectionState={oloWebhook.connectionState}
            lastReceivedAt={oloWebhook.lastReceivedAt}
            needsReconnect={oloWebhook.needsReconnect}
            onManage={onManageOloWebhook}
          />
        )}

        {/* Scheduled-order live updates aren't supported yet — a standing
            notice below the Olo webhook banner and above the status
            sections. Suppressed alongside it for an unsupported group,
            which replaces this whole area with the FR-76 billboard. */}
        {selectedConfiguration &&
          !isUnsupportedGroup &&
          !isLiveUpdatesDisabled && <ScheduledOrdersBanner />}

        {/* FR-44: server-supplied setup warnings (e.g. Live Activity / Apple
            Developer Program enrollment) surfaced on the page. */}
        {warnings.map(warning => (
          <Notification key={warning} kind="warning" size="full">
            {warning}
          </Notification>
        ))}

        {/* Publish result banners */}
        {config.publishSucceeded && !successDismissed && (
          <Notification
            kind="positive"
            size="full"
            closeable
            onClose={onDismissSuccess}
          >
            {t('publish_success')}
          </Notification>
        )}
        {renderPublishError()}

        {/* FR-76 unsupported-ordering billboard (Figma 1000:4376): when the
            whole configuration isn't configurable there is no order lifecycle
            to track, so a single locked billboard card replaces every status
            section. Lock header + explanatory body + the four disabled
            groups, each with its own lock icon. */}
        {isUnsupportedGroup && (
          <LockedCard
            title={tLocked('unsupported_title')}
            headingTag="h2"
            body={tLocked('unsupported_body')}
            rows={UNSUPPORTED_GROUP_KEYS.map(key => ({
              key,
              label: tLocked(`unsupported_groups.${key}`),
            }))}
          />
        )}

        {/* Status sections — omitted entirely for an unsupported (non-
            configurable) group, which renders only the billboard above.
            While a switch's detail hasn't caught up yet, `sectionPlans` may
            be `[]` or may still describe the PREVIOUS configuration for one
            render — a scoped placeholder here avoids either reading as real
            content for the configuration the picker now shows. A detail
            that will never resolve (unavailable) gets a retry notice in
            the same slot instead of a spinner that would spin forever. */}
        {!isUnsupportedGroup &&
          selectedConfiguration &&
          (isDetailUnavailable ? (
            <DetailStatusPlaceholder
              state="unavailable"
              data-testid="lu-configuration-sections-unavailable"
              retryAriaLabel={tErrors('retry_sections_aria')}
              onRetry={onReloadConfiguration}
            />
          ) : isDetailLoading ? (
            <DetailStatusPlaceholder
              state="loading"
              data-testid="lu-configuration-sections-loading"
              retryAriaLabel={tErrors('retry_sections_aria')}
              onRetry={onReloadConfiguration}
            />
          ) : (
            sectionPlans.map(renderSectionPlan)
          ))}

        {/* Help footer (Figma), deep-linked to the published Live Updates
            Zendesk article. */}
        <HelpCenterLink
          helpUrl={liveUpdatesHelpUrl}
          description={t('help.link')}
        />
      </div>

      {/* Sticky live preview. Kept mounted (with a scoped placeholder) while
          a switch's detail hasn't caught up yet instead of vanishing —
          `previewContent` may be `null`, or may still be the PREVIOUS
          configuration's real preview for one render (the same RTK Query
          gap as the sections column above), which must not render as if it
          belonged to the configuration the picker now shows. */}
      {(isLiveUpdatesDisabled ||
        previewContent ||
        ((isDetailLoading || isDetailUnavailable) &&
          selectedConfiguration)) && (
        <div
          data-testid="lu-configuration-preview-column"
          className={css({
            position: 'sticky',
            // Pin directly beneath the sticky header; as the header collapses
            // its height shrinks and the preview rides up by that same delta
            // (FR-62).
            top: previewTop,
            alignSelf: 'flex-start',
            marginTop: isLiveUpdatesDisabled
              ? PREVIEW_TOP_GAP_BELOW_DISABLED_BANNER
              : '0px',
            flex: `0 0 ${PREVIEW_WIDTH}`,
            maxWidth: '100%',
            // If the preview is taller than the remaining viewport it scrolls
            // independently rather than clipping the phone mock.
            maxHeight: `calc(100vh - ${previewTop} - ${
              isLiveUpdatesDisabled
                ? PREVIEW_BOTTOM_GAP_BELOW_DISABLED_BANNER
                : PREVIEW_BOTTOM_GAP
            })`,
            overflowY: 'auto',
          })}
        >
          {renderPreviewBody()}
        </div>
      )}
    </div>
  )
}

export default ConfigurationTab
